import { useEffect, useId, useRef } from 'react';
import { Btn, Card, ImageUpload } from '../ui';
import CardHeader from '../ui/headers/CardHeader';
import '../../css/SingleUnitContent.css';

export interface EquipmentCategoryFormCardProps {
    isCategoryEditMode: boolean;
    categoryName: string;
    onCategoryNameChange: (name: string) => void;
    categoryImagePreviewUrl: string;
    onCategoryImageSelect: (file: File) => void;
    /** When false, hides the card header (e.g. inside a modal). Defaults to true. */
    showHeader?: boolean;
    /** When true, focuses the equipment category name field on mount. */
    autoFocusFirstField?: boolean;
    onCancel: () => void;
    onSave: () => void;
    isSaving?: boolean;
    /** When false, renders form content only (e.g. inside a modal body). Defaults to true. */
    wrapInCard?: boolean;
    cardClassName?: string;
}

const CategoryFormBody = ({
    isCategoryEditMode,
    categoryName,
    onCategoryNameChange,
    categoryImagePreviewUrl,
    onCategoryImageSelect,
    onCancel,
    onSave,
    isSaving = false,
    showHeader = true,
    autoFocusFirstField = false,
}: Omit<EquipmentCategoryFormCardProps, 'wrapInCard' | 'cardClassName'>) => {
    const modeLabel = isCategoryEditMode ? 'Edit Equipment Category' : 'New Equipment Category';
    const equipmentImageLabelId = useId();
    const nameInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!autoFocusFirstField) return;
        const timer = window.setTimeout(() => nameInputRef.current?.focus(), 0);
        return () => window.clearTimeout(timer);
    }, [autoFocusFirstField]);

    return (
        <>
            {showHeader && (
                <CardHeader as="h2" title={categoryName.trim() || modeLabel} paddingVariant="pad-lg" />
            )}

            <div className="card-body">
                <div className="info-row editing priority-field-highlight">
                    <span className="info-label">Equipment Category Name *</span>
                    <span className="info-value-edit">
                        <input
                            ref={nameInputRef}
                            value={categoryName}
                            onChange={(event) => onCategoryNameChange(event.target.value)}
                            placeholder="e.g. Loaders, Excavators, Trucks..."
                            autoComplete="off"
                        />
                    </span>
                </div>
                <div className="info-row editing photo-thumbnail-row">
                    <span className="info-label" id={equipmentImageLabelId}>
                        Equipment Image
                    </span>
                    <div className="info-value-edit">
                        <ImageUpload
                            previewUrl={categoryImagePreviewUrl}
                            onSelect={onCategoryImageSelect}
                            previewAlt="Equipment image preview"
                            inputAriaLabel={
                                categoryImagePreviewUrl
                                    ? 'Change equipment image'
                                    : 'Upload equipment image'
                            }
                            labelId={equipmentImageLabelId}
                        />
                    </div>
                </div>
                <div className="actions">
                    <Btn variant="secondary" buttonText="Cancel" buttonOnClick={onCancel} />
                    <Btn
                        variant="primary"
                        buttonText={isSaving ? 'Saving...' : 'Save'}
                        buttonDisabled={isSaving}
                        buttonOnClick={onSave}
                    />
                </div>
            </div>
        </>
    );
};

export const EquipmentCategoryFormCard = ({
    wrapInCard = true,
    cardClassName = 'card-lg',
    ...formProps
}: EquipmentCategoryFormCardProps) => {
    if (!wrapInCard) {
        return <CategoryFormBody {...formProps} />;
    }

    return (
        <Card className={cardClassName}>
            <CategoryFormBody {...formProps} />
        </Card>
    );
};
